import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FACULTIES, YEAR_MIN, YEAR_MAX, RESULT_TYPES } from '@/lib/searchConstants';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../graph/graphConfig';
import SearchHelp from '../SearchHelp';

export type Namespace = 'person' | 'projects';

export interface BaseFilters {
  yearFrom?: number;
  yearTo?: number;
  faculties: string[];
  types?: string[];
}

interface GenericFilterRowProps<T extends BaseFilters> {
  namespace: Namespace;
  value: T;
  onChange: (next: T) => void;
  inert?: boolean;
}

export default function GenericFilterRow<T extends BaseFilters>({
  namespace,
  value,
  onChange,
  inert = false,
}: GenericFilterRowProps<T>) {
  const { t } = useTranslation(namespace);
  const { yearFrom, yearTo, faculties, types } = value;
  const supportsTypes = types !== undefined;

  const yearActive = yearFrom !== undefined || yearTo !== undefined;
  const facultyActive = faculties.length > 0;
  const typeActive = supportsTypes && (types?.length ?? 0) > 0;
  const anyActive = yearActive || facultyActive || typeActive;

  const yearLabel = yearActive
    ? `${yearFrom ?? YEAR_MIN}–${yearTo ?? YEAR_MAX}`
    : t('filters.all');

  const facultyLabel = facultyActive
    ? faculties.length <= 2
      ? faculties.join(', ')
      : `${faculties.slice(0, 2).join(', ')} +${faculties.length - 2}`
    : t('filters.all');

  const typeLabel = typeActive
    ? (types as string[]).length <= 1
      ? (types as string[])[0]
      : `${(types as string[])[0]} +${(types as string[]).length - 1}`
    : t('filters.all');

  const clearYears = () => onChange({ ...value, yearFrom: undefined, yearTo: undefined });
  const clearFaculty = () => onChange({ ...value, faculties: [] });
  const clearType = () => onChange({ ...value, types: [] });
  const reset = () =>
    onChange({
      ...value,
      yearFrom: undefined,
      yearTo: undefined,
      faculties: [],
      ...(supportsTypes ? { types: [] } : {}),
    });

  return (
    <div
      className={cn(
        'mb-3 flex items-center gap-1 border-b border-ctu-border/50 pb-2 transition-opacity',
        inert && 'opacity-40 pointer-events-none',
      )}
      title={inert ? t('filters.applyToCompetency') : undefined}
    >
      <FilterTrigger
        label={t('filters.years')}
        value={yearLabel}
        active={yearActive}
        onClear={yearActive ? clearYears : undefined}
        clearLabel={t('filters.clearYears')}
      >
        <YearRangePopover namespace={namespace} value={value} onChange={onChange} />
      </FilterTrigger>

      <FilterTrigger
        label={t('filters.faculty')}
        value={facultyLabel}
        active={facultyActive}
        onClear={facultyActive ? clearFaculty : undefined}
        clearLabel={t('filters.clearFaculty')}
      >
        <FacultyPopover namespace={namespace} value={value} onChange={onChange} />
      </FilterTrigger>

      {supportsTypes && (
        <FilterTrigger
          label={t('filters.type')}
          value={typeLabel}
          active={typeActive}
          onClear={typeActive ? clearType : undefined}
          clearLabel={t('filters.clearType')}
        >
          <TypePopover value={value} onChange={onChange} />
        </FilterTrigger>
      )}

      {anyActive && (
        <Button
          variant="ghost"
          size="sm"
          onClick={reset}
          className="ml-auto h-7 text-xs text-ctu-text-muted hover:text-ctu-text"
        >
          {t('filters.reset')}
        </Button>
      )}

      <div className={cn(!anyActive && 'ml-auto')}>
        <SearchHelp namespace={namespace} />
      </div>
    </div>
  );
}

interface FilterTriggerProps {
  label: string;
  value: string;
  active: boolean;
  onClear?: () => void;
  clearLabel: string;
  children: React.ReactNode;
}

function FilterTrigger({ label, value, active, onClear, clearLabel, children }: FilterTriggerProps) {
  return (
    <div className="flex items-center">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-7 px-2 text-xs font-normal text-ctu-text-muted hover:bg-muted/50 hover:text-ctu-text',
              active && 'text-ctu-text font-medium border-b border-primary rounded-none',
            )}
          >
            <span className="text-ctu-text-muted">{label}:</span>
            <span className="ml-1">{value}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72">
          {children}
        </PopoverContent>
      </Popover>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="inline-flex h-5 w-5 items-center justify-center rounded text-ctu-text-muted hover:bg-muted/50 hover:text-ctu-text"
          aria-label={clearLabel}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function YearRangePopover<T extends BaseFilters>({
  namespace,
  value,
  onChange,
}: {
  namespace: Namespace;
  value: T;
  onChange: (next: T) => void;
}) {
  const { t } = useTranslation(namespace);
  const [draft, setDraft] = useState<[number, number]>([
    value.yearFrom ?? YEAR_MIN,
    value.yearTo ?? YEAR_MAX,
  ]);

  const commit = (next: [number, number]) => {
    setDraft(next);
    onChange({
      ...value,
      yearFrom: next[0] === YEAR_MIN ? undefined : next[0],
      yearTo: next[1] === YEAR_MAX ? undefined : next[1],
    });
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-ctu-text-muted">
          {t('filters.yearRange')}
        </span>
        <span className="text-sm font-medium text-ctu-text tabular-nums">
          {draft[0]}–{draft[1]}
        </span>
      </div>
      <Slider
        min={YEAR_MIN}
        max={YEAR_MAX}
        step={1}
        value={draft}
        onValueChange={(v) => setDraft([v[0], v[1]] as [number, number])}
        onValueCommit={(v) => commit([v[0], v[1]] as [number, number])}
      />
      <div className="mt-2 flex justify-between text-[10px] text-ctu-text-muted">
        <span>{YEAR_MIN}</span>
        <span>{YEAR_MAX}</span>
      </div>
    </div>
  );
}

function FacultyPopover<T extends BaseFilters>({
  namespace,
  value,
  onChange,
}: {
  namespace: Namespace;
  value: T;
  onChange: (next: T) => void;
}) {
  const { t } = useTranslation(namespace);
  const toggle = (abbr: string) => {
    const next = value.faculties.includes(abbr)
      ? value.faculties.filter((f) => f !== abbr)
      : [...value.faculties, abbr];
    onChange({ ...value, faculties: next });
  };

  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ctu-text-muted">
        {t('filters.facultyHeading')}
      </div>
      <div className="flex flex-col gap-1 max-h-72 overflow-y-auto">
        {FACULTIES.map((abbr) => {
          const checked = value.faculties.includes(abbr);
          const color = FACULTY_COLORS[abbr] || DEFAULT_NODE_COLOR;
          return (
            <label
              key={abbr}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 hover:bg-muted/50"
            >
              <Checkbox checked={checked} onCheckedChange={() => toggle(abbr)} />
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: color }}
                aria-hidden
              />
              <span className="text-sm text-ctu-text">{abbr}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function TypePopover<T extends BaseFilters>({
  value,
  onChange,
}: {
  value: T;
  onChange: (next: T) => void;
}) {
  const { t } = useTranslation('projects');
  const types = value.types ?? [];
  const toggle = (typeName: string) => {
    const next = types.includes(typeName)
      ? types.filter((tn) => tn !== typeName)
      : [...types, typeName];
    onChange({ ...value, types: next });
  };

  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ctu-text-muted">
        {t('filters.typeHeading')}
      </div>
      <div className="flex flex-col gap-1 max-h-72 overflow-y-auto">
        {RESULT_TYPES.map((typeName) => {
          const checked = types.includes(typeName);
          return (
            <label
              key={typeName}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 hover:bg-muted/50"
            >
              <Checkbox checked={checked} onCheckedChange={() => toggle(typeName)} />
              <span className="text-sm text-ctu-text">{typeName}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
